import ContentSection from "../ContentSection";
import type { VoidComponent } from "solid-js";
import { createMemo, createSignal, Show, untrack } from "solid-js";
import { useTranslation } from "../../../state/i18n";
import RuleConditions from "../../../components/rule/RuleConditions";
import { useFormServices } from "../../../services/formServices";
import { makeEventListener } from "@solid-primitives/event-listener";
import type RuleShowEvent from "../../../events/RuleShowEvent";
import { isEmpty, isNil } from "lodash-es";
import FieldService from "../../../services/FieldService";
import ApiService from "../../../services/ApiService";
import { useFormVersionState } from "../../../state/formVersion";
import RuleActions from "../../../components/rule/RuleActions";
import { createOptionsForRuleConditions } from "../../../utils/selectOptionsUtil";
import withErrorBoundary from "../../../utils/withErrorBoundary";
import EmptyState from "../../../components/emptyState/EmptyState";
import SeparatorLine from "../../../components/SeparatorLine";

const RulesContent: VoidComponent = () => {
  const { t } = useTranslation();
  const { currentScope, fieldService, ruleService, validationService, variableService } = useFormServices();
  const { formId } = useFormVersionState();
  const rules = createMemo(() => ruleService.getAll());
  const [ruleId, setRuleId] = createSignal(untrack(() => rules()?.[0]?.id));

  const dataSourceInfosQuery = ApiService.getDataSourceInfosQuery(
    formId,
    // Columns for the Catalogue field are not configurable within rules
    () => !isEmpty(fieldService.getFieldsByType("search")),
  );
  const conditionOptions = createMemo(() => createOptionsForRuleConditions(currentScope, t));
  const actionFields = createMemo(() => fieldService.getFields());
  const actionVariables = createMemo(() => variableService.getVariables());

  const rule = createMemo(() => {
    const currentRuleId = ruleId();
    return isEmpty(currentRuleId) ? undefined : ruleService.get(currentRuleId);
  });

  const errors = createMemo(() => {
    const currentRuleId = ruleId();
    return isNil(currentRuleId) ? [] : validationService.getIssuesForRule(currentRuleId);
  });

  makeEventListener<Record<string, RuleShowEvent>>(document, "rule.show", (event) => {
    const rule = ruleService.get(event.ruleId);

    // Only handle events for current scope
    if (!isNil(rule)) {
      setRuleId(rule.id);
    }
  });

  return (
    <Show when={!dataSourceInfosQuery.isLoading}>
      <Show
        when={rule()}
        fallback={
          <EmptyState
            illustration={{ primaryIcon: "rulesIcon" }}
            title={t(($) => $.RULES_EMPTY_STATE_TITLE)}
            description={t(($) => $.RULES_EMPTY_STATE_DESCRIPTION)}
          />
        }
      >
        <div class="flex flex-col items-center">
          <TemplateBlock label={t(($) => $.RULES_CONTENT_IF)} />
          <SeparatorLine orientation="vertical" class="h-8" />
          <ContentSection>
            <Show when={rule()}>
              {(currentRule) => (
                <RuleConditions
                  scope={currentRule()?.scope}
                  options={conditionOptions()}
                  value={currentRule().conditions}
                  onChange={(value) => {
                    ruleService.changeConditions(currentRule().id, value);
                  }}
                  operator={currentRule().operator}
                  onOperatorChange={(operator) => ruleService.changeOperator(currentRule().id, operator)}
                  unscopedGetField={FieldService.getField}
                  getVariable={variableService.getVariable}
                  dataSources={dataSourceInfosQuery.data ?? []}
                  issues={errors()}
                />
              )}
            </Show>
          </ContentSection>
          <SeparatorLine orientation="vertical" class="h-8" />
          <TemplateBlock label={t(($) => $.RULES_CONTENT_THEN)} />
          <SeparatorLine orientation="vertical" class="h-8" />
          <ContentSection>
            <Show when={rule()}>
              {(currentRule) => (
                <RuleActions
                  fields={actionFields()}
                  variables={actionVariables()}
                  value={currentRule().actions}
                  onChange={(value) => {
                    ruleService.changeActions(currentRule().id, value);
                  }}
                  getField={FieldService.getField}
                  issues={errors()}
                />
              )}
            </Show>
          </ContentSection>
        </div>
      </Show>
    </Show>
  );
};

type TemplateBlockProps = {
  label: string;
};
const TemplateBlock: VoidComponent<TemplateBlockProps> = (props) => (
  <div class="flex items-center justify-center rounded-sm bg-surface-brand px-6 py-2 text-header-sm text-inverse uppercase">
    {props.label}
  </div>
);

const RulesContentWithErrorBoundary = withErrorBoundary(RulesContent);

export default RulesContentWithErrorBoundary;
